import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import type {
  QuestionnaireSection,
  SurveyInstance,
  TPrimaryKey,
} from '../types';

import { WORKSPACE_URLS } from '../constants';
import environment from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class SurveyService {
  private readonly _http = inject(HttpClient);

  /**
   * Fetches a survey instance for a given workspace.
   * @param workspaceId - The ID of the workspace to fetch the survey instance from.
   * @return An object containing the survey instance data.
   */
  getSurveyInstance(workspaceId: TPrimaryKey) {
    return this._http.get<SurveyInstance>(
      `${WORKSPACE_URLS.V2}${workspaceId}/survey/`,
    );
  }

  /**
   * Fetches a survey section for a given workspace, survey instance, and section ID.
   * @param workspaceId - The ID of the workspace to fetch the survey section from.
   * @param surveyInstaneId - The ID of the survey instance to fetch the survey section from.
   * @param sectionId - The ID of the section to fetch.
   * @return An object containing the survey section data, and the next and previous section links.
   */
  getSurveySection(
    workspaceId: TPrimaryKey,
    surveyInstanceId: TPrimaryKey,
    sectionId: TPrimaryKey,
  ) {
    return this._http.get<QuestionnaireSection>(
      `${WORKSPACE_URLS.V2}${workspaceId}/survey/${surveyInstanceId}/section/${sectionId}/`,
    );
  }

  /**
   * Answers a survey question for a given workspace, survey instance, and question ID.
   * @param workspaceId - The ID of the workspace to answer the question for.
   * @param surveyInstanceId - The ID of the survey instance to answer the question for.
   * @param questionId - The ID of the question to answer.
   * @param answer - The answer to the question, "closely related to 42".
   * @return An object containing the updated survey instance data.
   */
  answerSurveyQuestion(
    workspaceId: TPrimaryKey,
    surveyInstanceId: TPrimaryKey,
    questionId: TPrimaryKey,
    answer?: unknown,
  ) {
    return this._http.post<SurveyInstance>(
      `${WORKSPACE_URLS.V2}${workspaceId}/survey/${surveyInstanceId}/question/${questionId}/`,
      { answer },
    );
  }

  /**
   * Downloads the survey responses to PDF for a given workspace and survey instance.
   * @param workspaceId - The ID of the workspace to download the survey responses to PDF for.
   * @param surveyInstanceId - The ID of the survey instance to download the survey responses to PDF for.
   * @return A blob containing the survey responses to PDF.
   */
  downloadSurveyResponsesToPdf(
    workspaceId: TPrimaryKey,
    surveyInstanceId: TPrimaryKey,
  ) {
    return this._http.get<Blob>(
      `${WORKSPACE_URLS.V2}${workspaceId}/survey/${surveyInstanceId}/download/`,
      {
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  /**
   * @NOTE: Fetch Meta Values for each question
   * */
  fetchMetaValues(url: string) {
    return this._http.get<Record<string, unknown>[] | string[]>(
      `${environment.irisApi}${url}`,
    );
  }
}
